import dayjs from "dayjs";
import { CalendarAdvance, CalendarDayState, ExecutionCalendarDay } from "../../../../api/SiteDaily";

// Execution Calendar — pure helpers: the Monday-first month grid, state labels
// and the one-line summary of a day's advance.

export interface CalendarCell {
  date: string;
  inMonth: boolean;
}

/** Whole weeks covering `month` (YYYY-MM), Monday first. */
export const monthCells = (month: string): CalendarCell[] => {
  const first = dayjs(`${month}-01`);
  const offset = (first.day() + 6) % 7; // Monday = 0
  const start = first.subtract(offset, "day");
  const total = Math.ceil((offset + first.daysInMonth()) / 7) * 7;
  return Array.from({ length: total }, (_, i) => {
    const date = start.add(i, "day");
    return { date: date.format("YYYY-MM-DD"), inMonth: date.format("YYYY-MM") === month };
  });
};

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const stateLabel = (day: Pick<ExecutionCalendarDay, "state" | "approvalStatus" | "approvalLevelCount" | "approvalLevel">): string => {
  switch (day.state) {
    case "approved":
      return "Approved";
    case "review":
      return day.approvalLevelCount > 0 ? `Level ${day.approvalLevel || 1} review` : "Submitted";
    case "rejected":
      return "Rejected";
    case "entered":
      return day.approvalStatus === "Recalled" ? "Recalled" : day.approvalStatus === "Reopened" ? "Reopened" : "Entered";
    case "notEntered":
      return "Not entered";
    case "nonWorking":
      return "Non-working";
    case "today":
      return "Today";
    default:
      return "";
  }
};

export const LEGEND: Array<{ state: CalendarDayState; label: string }> = [
  { state: "approved", label: "Approved" },
  { state: "review", label: "Submitted or in review" },
  { state: "rejected", label: "Rejected, needs correcting" },
  { state: "entered", label: "Entered, not submitted" },
  { state: "notEntered", label: "Not entered" },
  { state: "nonWorking", label: "Non-working day or holiday" },
];

const trim = (qty: string) => {
  const n = Number(qty);
  return Number.isFinite(n) ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : qty;
};

export const advanceText = (advance: CalendarAdvance[], max = 2): string => {
  const parts = advance.map((a) => {
    const unit = a.hasStretches ? "m" : a.uomName;
    return `${a.slotLabel} ${trim(a.qty)}${unit ? ` ${unit}` : ""}`;
  });
  return parts.length > max ? `${parts.slice(0, max).join(" · ")} +${parts.length - max}` : parts.join(" · ");
};

export const shiftMonth = (month: string, delta: number) =>
  dayjs(`${month}-01`).add(delta, "month").format("YYYY-MM");
